import type { PrivateAgentProfile } from "@salesko/contracts";
import { Select } from "@salesko/design-system";
import { useI18n, type Locale } from "../../i18n";

export const privateAgentSelectionCopy = {
  en: {
    label: "Private Agent", choose: "Choose an Agent", required: "Choose an Agent before starting Chat or Research.",
    create: "Create Agent", unavailable: "The selected Agent is unavailable. Choose another Agent.",
    computer: "Choose a computer", fixedComputer: "This Agent stays on this computer. Reconnect the same computer in Computers.",
    checking: "Checking the selected Agent…", loadError: "Could not load Agents or readiness. Try again."
  },
  "zh-Hans": {
    label: "私人 Agent", choose: "选择 Agent", required: "请先选择 Agent，再开始 Chat 或 Research。",
    create: "创建 Agent", unavailable: "所选 Agent 不可用，请重新选择。",
    computer: "选择电脑", fixedComputer: "此 Agent 保留在这台电脑上。请在电脑设置中重新连接同一台电脑。",
    checking: "正在检查所选 Agent…", loadError: "无法加载 Agent 或就绪状态，请重试。"
  },
  "zh-Hant": {
    label: "私人 Agent", choose: "選擇 Agent", required: "請先選擇 Agent，再開始 Chat 或 Research。",
    create: "建立 Agent", unavailable: "所選 Agent 無法使用，請重新選擇。",
    computer: "選擇電腦", fixedComputer: "此 Agent 保留在這台電腦上。請在電腦設定中重新連接同一台電腦。",
    checking: "正在檢查所選 Agent…", loadError: "無法載入 Agent 或就緒狀態，請重試。"
  },
  ja: {
    label: "プライベート Agent", choose: "Agent を選択", required: "Chat または Research を始める前に Agent を選択してください。",
    create: "Agent を作成", unavailable: "選択した Agent は利用できません。別の Agent を選択してください。",
    computer: "コンピューターを選択", fixedComputer: "この Agent はこのコンピューターで動作します。設定から同じコンピューターを再接続してください。",
    checking: "選択した Agent を確認中…", loadError: "Agent または準備状態を読み込めませんでした。再試行してください。"
  }
} satisfies Record<Locale, Record<string, string>>;

/** The host restores explicit user choice; this control never chooses the first Agent. */
export function PrivateAgentSelector({ agents, agentId, onChange, disabled = false }: {
  agents: ReadonlyArray<Pick<PrivateAgentProfile, "agentId" | "name">>;
  agentId: string | undefined;
  onChange: (agentId: string | undefined) => void;
  disabled?: boolean;
}) {
  const { locale } = useI18n();
  const copy = privateAgentSelectionCopy[locale];
  return <Select
    label={copy.label}
    placeholder={copy.choose}
    disabled={disabled}
    value={agentId ?? ""}
    onChange={(value) => onChange(value || undefined)}
    options={[
      { label: copy.choose, value: "" },
      ...agents.map((agent) => ({ label: agent.name, value: agent.agentId }))
    ]}
  />;
}
